/**
 * Cookie Helpers - Secure cookie management utilities
 * Implements best practices for cookie handling, consent, and security
 */

export interface CookieConsent {
  essential: boolean;  // Always true (required for functionality)
  functional: boolean; // User preferences, personalization
  analytics: boolean;  // GA, tracking
  marketing: boolean;  // FB pixel, retargeting
}

export interface CookieOptions {
  maxAge?: number;      // seconds
  expires?: Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  priority?: 'Low' | 'Medium' | 'High';
}

const COOKIE_VERSION = 'v2';

/**
 * Get cookie consent state from localStorage
 */
export function getCookieConsent(): CookieConsent {
  const consent = localStorage.getItem('mm_cookie_consent');

  if (!consent) {
    // No consent recorded yet - show banner
    return {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false
    };
  }

  // Handle legacy 'accepted' string value
  if (consent === 'accepted') {
    return {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true
    };
  }

  // Parse JSON consent object
  try {
    const parsed = JSON.parse(consent);
    return {
      essential: true,
      functional: parsed.functional ?? false,
      analytics: parsed.analytics ?? false,
      marketing: parsed.marketing ?? false
    };
  } catch (e) {
    console.error('Failed to parse cookie consent:', e);
    // Default: only essential cookies allowed
    return {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false
    };
  }
}

/**
 * Update GTM Consent Mode v2 state so GA4/ads tags respect user choice.
 * Must be called after every consent change AND on page load if consent was
 * previously granted (the HTML default is 'denied').
 */
function updateGtmConsent(analytics: boolean, marketing: boolean): void {
  if (typeof window === 'undefined') return;
  const dl = (window as any).dataLayer;
  if (!Array.isArray(dl)) return;

  // gtag helper already defined in <head> of index.html
  const gtag: (...args: any[]) => void =
    typeof (window as any).gtag === 'function'
      ? (window as any).gtag
      : (...args: any[]) => { dl.push(args); };

  gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied'
  });
}

/**
 * Restore GTM consent from localStorage on page load.
 * Call this early in the app lifecycle so GA4 starts collecting immediately
 * for returning visitors who already accepted cookies.
 */
export function restoreGtmConsent(): void {
  const consent = getCookieConsent();
  if (consent.analytics || consent.marketing) {
    updateGtmConsent(consent.analytics, consent.marketing);
  }
}

/**
 * Set cookie consent state
 */
export function setCookieConsent(consent: Partial<CookieConsent>): void {
  const fullConsent: CookieConsent = {
    essential: true,
    functional: consent.functional ?? false,
    analytics: consent.analytics ?? false,
    marketing: consent.marketing ?? false
  };

  localStorage.setItem('mm_cookie_consent', JSON.stringify(fullConsent));
  localStorage.setItem('mm_cookie_consent_date', new Date().toISOString());

  // Inform GTM of the updated consent state
  updateGtmConsent(fullConsent.analytics, fullConsent.marketing);
}

/**
 * Parse cookies from document.cookie string
 */
export function parseCookies(cookieString?: string): Record<string, string> {
  const str = cookieString || (typeof document !== 'undefined' ? document.cookie : '');
  const cookies: Record<string, string> = {};

  if (!str) return cookies;

  str.split(';').forEach(cookie => {
    const [name, ...rest] = cookie.split('=');
    const value = rest.join('=').trim();
    if (name) {
      cookies[name.trim()] = decodeURIComponent(value);
    }
  });

  return cookies;
}

/**
 * Get a specific cookie value
 */
export function getCookie(name: string): string | null {
  const cookies = parseCookies();
  return cookies[name] || null;
}

/**
 * Set a client-side cookie (NOT HttpOnly)
 * Use this for non-sensitive data only
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): boolean {
  const {
    maxAge = 31536000, // 1 year default
    path = '/',
    secure = true,
    sameSite = 'Lax',
    priority
  } = options;

  const cookieParts = [`${name}=${encodeURIComponent(value)}`];

  if (maxAge) cookieParts.push(`max-age=${maxAge}`);
  if (options.expires) cookieParts.push(`expires=${options.expires.toUTCString()}`);
  cookieParts.push(`path=${path}`);
  if (options.domain) cookieParts.push(`domain=${options.domain}`);
  if (secure) cookieParts.push('Secure');
  cookieParts.push(`SameSite=${sameSite}`);
  if (priority) cookieParts.push(`Priority=${priority}`);

  const cookieString = cookieParts.join('; ');

  // Check size before setting (4KB limit)
  if (cookieString.length > 4000) {
    console.warn(`Cookie ${name} too large: ${cookieString.length} bytes`);
    return false;
  }

  if (typeof document !== 'undefined') {
    document.cookie = cookieString;
  }

  return true;
}

/**
 * Delete a cookie
 */
export function deleteCookie(name: string, path: string = '/'): void {
  if (typeof document !== 'undefined') {
    document.cookie = `${name}=; max-age=0; path=${path}`;
  }
}

/**
 * Set versioned profile cookie with JSON data
 */
export function setProfileCookie(profileData: any, maxAgeDays: number = 30): boolean {
  const consent = getCookieConsent();

  if (!consent.functional) {
    // requires functional consent
    return false;
  }

  const versionedData = {
    v: 2,
    ...profileData,
    last_seen: Date.now()
  };

  return setCookie(`mm_profile_${COOKIE_VERSION}`, JSON.stringify(versionedData), {
    maxAge: maxAgeDays * 24 * 60 * 60,
    priority: 'High',
    path: '/'
  });
}

/**
 * Get versioned profile cookie
 */
export function getProfileCookie(): any | null {
  // Try v2 first
  const v2Cookie = getCookie(`mm_profile_${COOKIE_VERSION}`);
  if (v2Cookie) {
    try {
      return JSON.parse(v2Cookie);
    } catch (e) {
      console.error('Failed to parse v2 profile cookie:', e);
    }
  }

  // Try v1 (legacy)
  const v1Cookie = getCookie('mm_profile');
  if (v1Cookie) {
    try {
      return JSON.parse(v1Cookie);
    } catch (e) {
      console.error('Failed to parse v1 profile cookie:', e);
    }
  }

  return null;
}

/**
 * Set analytics/marketing cookie (requires consent)
 */
export function setAnalyticsCookie(name: string, value: string, maxAgeDays: number = 365): boolean {
  const consent = getCookieConsent();

  if (!consent.analytics && !consent.marketing) {
    // requires consent
    return false;
  }

  return setCookie(name, value, {
    maxAge: maxAgeDays * 24 * 60 * 60,
    priority: 'Low',
    path: '/'
  });
}

/**
 * Check if a cookie exists
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}

/**
 * Get cookie size in bytes
 */
export function getCookieSize(name: string): number {
  const value = getCookie(name);
  if (!value) return 0;
  return new Blob([`${name}=${value}`]).size;
}

/**
 * Get total cookie size for domain
 */
export function getTotalCookieSize(): number {
  if (typeof document === 'undefined') return 0;
  return new Blob([document.cookie]).size;
}

/**
 * Clear all Moon Manifest cookies
 */
export function clearAllMMCookies(): void {
  const cookies = parseCookies();
  Object.keys(cookies).forEach(name => {
    if (name.startsWith('mm_') || name.startsWith('__Host-mm_') || name.startsWith('__Secure-mm_')) {
      deleteCookie(name);
    }
  });
}
